<template>
  <div class="d-comment-entry">
    <DCommentBox
      v-if="isEditing"
      :auto-focus="true"
      :comment-slug="comment.id"
      :item-slug="itemSlug"
      :item-type="itemType"
      :mentions="comment.mentions"
      placeholder="Edit your comment"
      :user-avatar-url="comment.avatar"
      :user-name="comment.user"
      :value="comment.body"
      @comment-edited="commentEdited"
    />

    <div
      v-else
      class="comment-item grid-flex grid-no-response"
    >
      <DUserLinkPopup
        :slug="comment.user_slug"
        :url="comment.user_url"
      >
        <template slot="content">
          <div class="grid-item-shrink avatar">
            <img :src="comment.avatar">
          </div>
        </template>
      </DUserLinkPopup>

      <div class="grid-item">
        <div class="comment-content">
          <DUserLinkPopup
            :slug="comment.user_slug"
            :url="comment.user_url"
          >
            <template slot="content">
              <a
                class="comment-author"
                :href="comment.user_url"
              >{{ comment.user }}</a>
            </template>
          </DUserLinkPopup>
          –
          <DTextWithLinks
            class="comment-body"
            :content="comment.body"
            :display-multiline="true"
            :display-typed-links="true"
            :mentions="comment.mentions"
          />
        </div>

        <div class="comment-info grid-flex grid-no-response">
          <div class="grid-item actions">
            <a
              class="upvote-action icon-action"
              :disabled="(isProcessing || comment.is_owner)"
              title="Upvote"
              @click="upvote"
            >
              <DuiIconLike
                class="icon-sm push-up"
                :class="{
                  'icon-yellow': comment.is_upvoted,
                  'icon-grey-dark': !comment.is_upvoted,
                }"
                style="margin-top: -2px;"
              />
            </a>

            <span class="num-upvotes action-label valign-middle">
              <a
                v-if="!!comment.upvotes"
                @click="showUpvotes"
              >
                {{ comment.upvotes }} {{ comment.upvotes | duiPluralize('like', 'likes') }}
              </a>
              <span v-else>0 likes</span>
            </span>

            <a
              :disabled="isProcessing"
              @click="reply"
            >
              Reply
            </a>

            <span v-if="comment.is_owner">
              <a
                :disabled="isProcessing"
                @click="editComment"
              >
                Edit
              </a>

              <a
                :disabled="isProcessing"
                @click="deleteComment"
              >
                Delete
              </a>
            </span>

            <a
              v-if="(isUserSignedIn && !comment.is_owner)"
              :disabled="(comment.is_reported || isProcessing)"
              @click="reportComment"
            >
              <span v-if="comment.is_reported">Reported</span>
              <span v-else>Report</span>
            </a>
          </div>

          <div class="grid-item-shrink time-ago">
            {{ comment.time_ago }} ago
          </div>
        </div>
      </div>
    </div>

    <DCommentEntryRemoval
      v-if="isDeletingComment"
      :comment="isDeletingComment"
      :item-slug="itemSlug"
      :item-type="itemType"
      @comment-removed="commentRemoved"
      @dismiss="dismissCommentRemoval"
    />

    <DCommentEntryReport
      v-if="isReportingComment"
      :comment="isReportingComment"
      :item-slug="itemSlug"
      :item-type="itemType"
      @dismiss="dismissCommentReport"
    />

    <DUpvotesModal
      v-if="isUpvotesDisplayed"
      :is-user-signed-in="isUserSignedIn"
      :item-type="`${ itemType }-comment`"
      :item-slug="comment.id"
      :parent-slug="itemSlug"
      @dismiss="dismissUpvotes"
    />
  </div>
</template>

<script>
import Vue from 'vue';

import commentsService from '@d-services/comments-service/comments-service';
import DCommentBox from '@d-components/d-comments-panel/d-comment-box/d-comment-box.vue';
import DCommentEntryRemoval from './d-comment-entry-removal/d-comment-entry-removal.vue';
import DCommentEntryReport from './d-comment-entry-report/d-comment-entry-report.vue';
import DTextWithLinks from '@d-components/d-text-with-links/d-text-with-links.vue';
import DUpvotesModal from '@d-components/modals/d-upvotes-modal/d-upvotes-modal.vue';
import DUserLinkPopup from '@d-components/d-user-link-popup/d-user-link-popup.vue';
import { getErrorMessage } from '@d-shared/utils/errors';

export default {
  components: {
    DCommentBox,
    DCommentEntryRemoval,
    DCommentEntryReport,
    DTextWithLinks,
    DUpvotesModal,
    DUserLinkPopup,
  },
  props: {
    comment: {
      type: Object,
      required: true,
    },
    isUserSignedIn: {
      type: Boolean,
      default: false,
    },
    itemSlug: {
      type: String,
      required: true,
    },
    itemType: {
      type: String,
      validator: function (value) {
        return [
          'book',
          'post',
          'user-review',
        ].indexOf(value) !== -1;
      },
      required: true,
    },
  },
  data: () => ({
    isDeletingComment: null,
    isEditing: false,
    isProcessing: false,
    isReportingComment: null,
    isUpvotesDisplayed: false,
  }),
  methods: {
    async addUpvote() {
      this.$set(this, 'isProcessing', true);

      try {
        switch (this.itemType) {
          case 'book':
            await commentsService.addBookCommentUpvote(this.itemSlug, this.comment.id);
            break;
          case 'post':
            await commentsService.addPostCommentUpvote(this.itemSlug, this.comment.id);
            break;
          case 'user-review':
            await commentsService.addUserReviewCommentUpvote(this.itemSlug, this.comment.id);
            break;
        }

        this.$set(this.comment, 'is_upvoted', true);
        this.$set(this.comment, 'upvotes', this.comment.upvotes + 1);
        Vue.duiNotificationManager.success('Comment upvoted!');
      } catch (error) {
        Vue.duiNotificationManager.error(getErrorMessage(error));
      }

      this.$set(this, 'isProcessing', false);
    },

    commentEdited(editedComment) {
      this.$set(this.comment, 'body', editedComment.body);
      this.$set(this.comment, 'mentions', editedComment.mentions);
      this.$set(this, 'isEditing', false);
    },

    async commentRemoved(comment) {
      this.dismissCommentRemoval();
      await this.$nextTick();
      this.$emit('comment-removed', comment);
    },

    deleteComment() {
      this.$set(this, 'isDeletingComment', this.comment);
    },

    dismissCommentRemoval() {
      this.$set(this, 'isDeletingComment', null);
    },

    dismissCommentReport() {
      this.$set(this, 'isReportingComment', null);
    },

    dismissUpvotes() {
      this.$set(this, 'isUpvotesDisplayed', false);
    },

    editComment() {
      this.$set(this, 'isEditing', true);
    },

    async removeUpvote() {
      this.$set(this, 'isProcessing', true);

      try {
        switch (this.itemType) {
          case 'book':
            await commentsService.removeBookCommentUpvote(this.itemSlug, this.comment.id);
            break;
          case 'post':
            await commentsService.removePostCommentUpvote(this.itemSlug, this.comment.id);
            break;
          case 'user-review':
            await commentsService.removeUserReviewCommentUpvote(this.itemSlug, this.comment.id);
            break;
        }

        this.$set(this.comment, 'is_upvoted', false);
        this.$set(this.comment, 'upvotes', this.comment.upvotes - 1);
        Vue.duiNotificationManager.success('Upvote removed.');
      } catch (error) {
        Vue.duiNotificationManager.error(getErrorMessage(error));
      }

      this.$set(this, 'isProcessing', false);
    },

    reply() {
      if (!this.isUserSignedIn) {
        Vue.prototype.signupModal.show();
        return;
      }

      this.$emit('reply', this.comment);
    },

    reportComment() {
      this.$set(this, 'isReportingComment', this.comment);
    },

    showUpvotes() {
      if (!this.isUserSignedIn) {
        Vue.prototype.signupModal.show();
        return;
      }

      this.$set(this, 'isUpvotesDisplayed', true);
    },

    upvote() {
      if (!this.isUserSignedIn) {
        Vue.prototype.signupModal.show();
        return;
      }

      if (this.comment.is_owner) {
        return;
      }

      if (this.comment.is_upvoted) {
        this.removeUpvote();
        return;
      }

      this.addUpvote();
    },
  },
};
</script>

<style lang="scss">
@import "@dui-styles/base";

.d-comment-entry {
  .comment-content {
    font-size: $font-size-sm;

    a.comment-author {
      color: getColor($black);
    }
  }

  .comment-info {
    font-size: $font-size-xs;

    a,
    .action-label,
    .time-ago {
      color: getColor($grey, dark);
    }

    a,
    .action-label {
      display: inline-flex;
      margin-right: $_0-5em;
    }

    a {
      vertical-align: middle;

      @include transition((
        color
      ));

      &:hover {
        color: getColor($black);

        .dui-icon {
          #icon-fill {
            fill: getColor($black);
          }
          #icon-stroke {
            stroke: getColor($black);
          }
        }
      }

      &[disabled] {
        opacity: $disabled-opacity;
        pointer-events: none;
      }
    }
  }

  .actions {
    .num-upvotes {
      margin-left: -$_0-5em;
    }

    .icon-action {
      .text {
        vertical-align: middle;
      }
    }
  }
}
</style>
